import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { sortByLabel } from "@/lib/sortRows";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Each status carries a hue used for its dot everywhere in the app, plus a
// bg/text/border trio used by StatusBadge. The trio follows one recipe —
// bg-{hue}-50 / text-{hue}-700 / border-{hue}-200 — so the set reads as a family
// instead of a handful of ad-hoc tints. Before this, the tint levels were
// inconsistent (slate-100/700/300 next to blue-50/800/200 next to
// emerald-50/700/200), which is what made the palette look unconsidered at a
// glance. Hues are unchanged, so no status changes meaning anywhere.
//
// The hue order is the workflow itself: neutral grey before work starts, cool
// hues while it moves through the pipeline, warm at cutover, green when live,
// amber for the side-state.
export const STATUS_STYLES = {
  // Task status: Open → WIP → Test → UAT → Cutover → Live, with Hold as a
  // side-state at any point.
  open:    { label: "Open",    dot: "#64748B", bg: "bg-slate-100",  text: "text-slate-700",   border: "border-slate-200" },
  wip:     { label: "WIP",     dot: "#0176D3", bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  test:    { label: "Test",    dot: "#7C3AED", bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200" },
  uat:     { label: "UAT",     dot: "#0891B2", bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200" },
  cutover: { label: "Cutover", dot: "#EA580C", bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200" },
  live:    { label: "Live",    dot: "#059669", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },

  // Project status: Initiation → Planning → Execution → Closed, with Hold
  // as a side-state at any point. "hold" and "closed" are shared between tasks
  // and projects — one style each, same meaning either way.
  initiation: { label: "Initiation", dot: "#64748B", bg: "bg-slate-100",  text: "text-slate-700",   border: "border-slate-200" },
  planning:   { label: "Planning",   dot: "#4F46E5", bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200" },
  execution:  { label: "Execution",  dot: "#0176D3", bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  // Closed is the one deliberate exception to the 50/700/200 recipe: it is a
  // DEEPER emerald than Live rather than a different hue, because since tasks
  // gained a Closed status the two sit next to each other in the same dropdown
  // and the same pie chart. Same family (both mean "finished"), separable at a
  // glance; a different hue would have implied a different kind of outcome.
  closed:     { label: "Closed",     dot: "#047857", bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  hold:       { label: "Hold",       dot: "#D97706", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },

  // Cancelled — amber, as asked for, but a DEEPER amber than Hold rather than
  // the same one. The two sit next to each other in the task dropdown and in
  // the same pie chart, and identical amber would make "paused" and "dropped"
  // indistinguishable at a glance. Same family (both mean "not progressing"),
  // separable by depth — the same treatment Closed gets against Live.
  cancelled:  { label: "Cancelled",  dot: "#B45309", bg: "bg-amber-100",  text: "text-amber-800",   border: "border-amber-300" },

  // Initiative status (initiative_status enum) — untouched, unrelated to
  // the project/task status redesign above; kept as its own set of keys
  // since "active"/"on_hold"/"completed" only ever mean initiative status
  // now that projects have moved to initiation/planning/execution/closed.
  active:    { label: "Active",    dot: "#0176D3", bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  on_hold:   { label: "On Hold",   dot: "#D97706", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  completed: { label: "Completed", dot: "#059669", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
};

export const PRIORITY_STYLES = {
  low: { label: "Low", cls: "text-zinc-600 border-zinc-300" },
  medium: { label: "Medium", cls: "text-blue-800 border-blue-300" },
  high: { label: "High", cls: "text-amber-800 border-amber-300" },
  urgent: { label: "Urgent", cls: "text-red-700 border-red-300" },
};

// One pill recipe, exported so a read-only status chip and an editable status
// dropdown trigger can't drift into looking like two different things on the
// same screen. Callers add their own horizontal padding, because a trigger with
// a caret needs less on the right than a plain chip does.
//
// The pill body stays neutral — zinc fill, zinc border, zinc text, the same for
// every status. All of the colour lives in the dot, which makes it the only
// accent in a row and keeps seven pills from competing with each other.
export function statusPillClass() {
  return "inline-flex items-center gap-1.5 h-6 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-700 text-[11px] font-medium whitespace-nowrap";
}

// The dot carries each status's own hue, straight from STATUS_STYLES, so the
// pill, the metrics strip and StatusBadge on the other pages all agree on what
// colour a given status is.
export function StatusDot({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.open;
  return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: s.dot }} />;
}

// The small rounded pill — used on its own for a status nobody is allowed to
// change, and inside the dropdown as each option, so every choice carries its
// own colour rather than sitting as plain text in a white list.
export function StatusPill({ status, className = "", testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.open;
  return (
    <span data-testid={testid} className={`${statusPillClass()} px-2.5 ${className}`}>
      <StatusDot status={status} />
      {s.label}
    </span>
  );
}

export function StatusBadge({ status, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.open;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-sm border ${s.bg} ${s.text} ${s.border}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {s.label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const p = PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border bg-white ${p.cls}`}>
      {p.label}
    </span>
  );
}

// The editable status: a pill that opens a menu of pills. The trigger IS the
// chip — a <Select> renders a fixed-width bordered box, which reads as a form
// field dropped into a table row rather than as state you can change.
//
// Shared because two grids need it (Tasks, and the task list on a project),
// and a second copy would be the start of them drifting apart.
export function StatusSelectPill({ status, statuses, onChange, disabled = false, testid }) {
  const st = STATUS_STYLES[status] || STATUS_STYLES.open;
  if (disabled) return <StatusPill status={status} testid={testid} />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          aria-label={`Status ${st.label} — change it`}
          className={`${statusPillClass()} pl-2.5 pr-1.5 cursor-pointer transition-shadow hover:shadow-sm hover:border-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0176D3]/40`}
        >
          <StatusDot status={status} />
          {st.label}
          <CaretDownIcon size={10} className="opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-auto min-w-[136px] p-1">
        {sortByLabel(statuses, (x) => STATUS_STYLES[x]?.label || x).map((x) => (
          <DropdownMenuItem
            key={x}
            onSelect={() => onChange(x)}
            data-testid={testid ? `${testid}-${x}` : undefined}
            className="p-1 gap-2 rounded-sm cursor-pointer"
          >
            <StatusPill status={x} />
            {x === status && <CheckIcon size={12} className="ml-auto shrink-0 text-[#0176D3]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
